import React, {useState} from 'react';
import classNames from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isRTLString} from '@priority-software/priority-utils/lib/strings';
import {
    Title,
    Flex,
    Icon,
    PriorityIcon,
    Separator,
    Text,
    Popover,
    PopoverPlacement,
    Button
} from '@priority-software/priority-style-react';
import styles from './styles.module.scss';
import {PanelSettings} from '../PanelSettings';
import texts from '../../texts';
import {BasicFormData} from '../../constants';
import {isRTL} from '../../../../Localization';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import {sendMonitor} from '../../../../utils/monitoring';
import {Tooltip} from '../../../../components/Common/Tooltip/index';
import {createDataTestId} from '../../../../utils/dataTestId';

export interface FormData extends BasicFormData {
    cols: Array<priority.netitems.form.IColumn>;
    fetchValue: string;
    fields: Array<any>;
    formTitle: string;
    zoomName?: string;
    isEmpty?: boolean;
}

type Props = {
    panelTitle: string;
    entityDescription: string;
    entityNumber: string;
    closeInfo: () => void;
    shownForms: FormData[];
    updateInfoMap: (forms: FormData[]) => void;
    zoomFname: string;
    settingsMenuRef: React.RefObject<HTMLDivElement>;
    disable: boolean;
};

const HelpPopover = () => {
    return (
        <Popover
            {...createDataTestId('popover-help-dialog-container', true)}
            clickable
            closable
            hoverable={false}
            rtl={isRTL()}
            icon={PriorityIcon.questionCircle}
            title={
                <div {...createDataTestId('popover-help-dialog-title', false)}>
                    {texts.help_label}
                </div>
            }
            content={
                <div {...createDataTestId('popover-help-dialog-text', false)}>
                    {texts.help_content}
                </div>
            }
            zIndex={getTopDialogZindex()}
            placement={PopoverPlacement.bottomEnd}
            footer={onHide => (
                <Flex justifyContent="flex-end">
                    <Button skin="primary" onClick={onHide} autoFocus>
                        {texts.ok_label}
                    </Button>
                </Flex>
            )}
        >
            <Icon icon={PriorityIcon.help} componentClasses={styles.helpIcon} />
        </Popover>
    );
};

export const DialogHeader = ({
    panelTitle,
    entityDescription,
    entityNumber,
    closeInfo,
    shownForms,
    updateInfoMap,
    zoomFname,
    settingsMenuRef,
    disable
}: Props) => {
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const rtl = isRTL();

    const onEdit = () => {
        sendMonitor('MoreDetailsSettings');
        setIsEdit(true);
    };

    return (
        <>
            <div className={styles.header}>
                <Flex>
                    <Title
                        textColor="secondary-500"
                        skin="h4"
                        componentClasses={styles.headerTitle}
                    >
                        {panelTitle}
                    </Title>
                    <Flex justifyContent="flex-end">
                        <HelpPopover />

                        <Tooltip
                            position={rtl ? 'bottom left' : 'bottom right'}
                            delay={100}
                            tooltip={!isEdit && !disable && texts.settings_tooltip}
                            zIndex={getTopDialogZindex()}
                        >
                            <div ref={settingsMenuRef}>
                                <Icon
                                    icon={PriorityIcon.settings}
                                    onClick={onEdit}
                                    componentClasses={classNames(styles.settingsIcon, {
                                        [styles.activeSettings]: isEdit,
                                        [styles.disabledSettings]: disable
                                    })}
                                    iconColor="primary-700"
                                />
                                {isEdit && shownForms.length > 0 && (
                                    <PanelSettings
                                        isOpen={isEdit}
                                        closeSettings={() => setIsEdit(false)}
                                        currentShownForms={shownForms}
                                        updateInfoMap={updateInfoMap}
                                        zoomFname={zoomFname}
                                        menuRef={settingsMenuRef}
                                    />
                                )}
                            </div>
                        </Tooltip>
                        <Separator height={24} componentClasses={styles.separator} />
                        <Icon
                            icon={PriorityIcon.closeBig}
                            onClick={closeInfo}
                            iconColor="primary-700"
                        />
                    </Flex>
                </Flex>
                <Title
                    textColor="primary-500"
                    skin="h2"
                    componentClasses={classNames({
                        ltrValueStyles: rtl && !isRTLString(entityDescription),
                        rtlValueStyles: !rtl && isRTLString(entityDescription)
                    })}
                >
                    {entityDescription}
                </Title>
                <Text
                    tag="div"
                    skin="paragraph-2"
                    textColor="secondary-500"
                    componentClasses={classNames({
                        ltrValueStyles: rtl && !isRTLString(entityNumber),
                        rtlValueStyles: !rtl && isRTLString(entityNumber)
                    })}
                >
                    {entityNumber}
                </Text>
            </div>
        </>
    );
};
